import ApiService from './api'

class ReminderService {
  constructor() {
    this.intervalId = null
    this.processedReminders = new Set(JSON.parse(localStorage.getItem('notifiedReminders') || '[]'))
    this.token = null
    this.reminders = []
  }

  requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notification')
      return Promise.resolve('denied')
    }
    
    if (Notification.permission === 'granted') {
      return Promise.resolve('granted')
    }

    return Notification.requestPermission()
  }

  start(token) {
    this.token = token
    if (this.intervalId) return
    
    // Initial fetch from API
    this.refresh()
    
    // Check local cache every 10 seconds (increased frequency for better responsiveness)
    this.intervalId = setInterval(() => this.check(), 10000)
    
    console.log('Reminder service started (local cache mode). Notification support:', 'Notification' in window)
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  async refresh() {
      if (!this.token) return
      try {
          const api = ApiService
          const response = await api.getUpcomingReminders(this.token)
          this.reminders = response.links || []
          console.log(`[ReminderService] Refreshed. Found ${this.reminders.length} reminders.`)
          this.check()
      } catch (e) {
          console.error('Failed to refresh reminders', e)
      }
  }

  // Update local cache when a user updates a bookmark
  upsertLink(link) {
      if (!link) return
      
      const index = this.reminders.findIndex(l => l.id === link.id)
      
      // If it has a reminder, add/update it
      if (link.reminder_at) {
          if (index >= 0) {
              this.reminders[index] = link
          } else {
              this.reminders.push(link)
          }
           console.log(`[ReminderService] Updated reminder for "${link.title}"`)
      } else {
          // If reminder was removed, remove from list
          if (index >= 0) {
              this.reminders.splice(index, 1)
              console.log(`[ReminderService] Removed reminder for "${link.title}"`)
          }
      }
      
      // Re-check immediately in case it's now due
      this.check()
  }

  check() {
    if (!this.token) return
    
    const now = new Date()
    // console.log('[ReminderService] Check at', now.toLocaleTimeString())

    this.reminders.forEach(link => {
        if (!link.reminder_at) return
        
        let reminderTime
        let raw = link.reminder_at
        
        // Safety: Convert numeric strings to numbers
        if (typeof raw === 'string' && !isNaN(raw) && !raw.includes('-') && !raw.includes(':')) {
           raw = Number(raw)
        }

        // Handle Unix timestamp (seconds) which are typically < 100 billion (year 5138)
        // Milliseconds for year 2000+ are > 946 billion
        if (typeof raw === 'number') {
            if (raw < 100000000000) {
                reminderTime = new Date(raw * 1000)
            } else {
                reminderTime = new Date(raw)
            }
        } else {
             // Fallback for ISO strings or other formats
             reminderTime = new Date(raw)
        }
        
        // Validate date
        if (isNaN(reminderTime.getTime())) {
            console.warn(`[ReminderService] Invalid date for "${link.title}":`, link.reminder_at)
            return
        }
        
        const key = `${link.id}-${reminderTime.getTime()}`
        
        if (this.processedReminders.has(key)) {
            // console.log(`[ReminderService] Skipping already processed: ${link.title}`)
            return
        }
        
        const diff = reminderTime - now
        // console.log(`[ReminderService] checking "${link.title}": due in ${Math.round(diff/1000)}s`)

        // Check if due: reminderTime <= now
        if (reminderTime <= now) {
            // Check if it's not too old (e.g., more than 24 hours ago)
            const oneDay = 24 * 60 * 60 * 1000
            if (now - reminderTime < oneDay) {
                console.log(`[ReminderService] Reminder DUE: "${link.title}"`)
                // Try to notify. If successful (or permission denied/ignored), mark as processed.
                // If permission is default (pending), we don't mark as processed so we can try again when permission is granted.
                const handled = this.notify(link)
                if (handled) {
                  console.log(`[ReminderService] Notification handled for "${link.title}"`)
                  this.processedReminders.add(key)
                } else {
                  console.log(`[ReminderService] Notification NOT handled (permission pending?) for "${link.title}"`)
                }
            } else {
                // Too old, just mark as processed to stop checking
                console.log(`[ReminderService] Reminder too old: "${link.title}"`)
                this.processedReminders.add(key)
            }
        }
    })
      
    this.saveProcessedState()
  }
  
  notify(link) {
    console.log(`[ReminderService] notify() called for "${link.title}". Permission: ${Notification.permission}`)
    
    // Always dispatch an in-app event for the UI to pick up (Toast/Snackbar)
    // This ensures visibility even if OS notifications are suppressed
    window.dispatchEvent(new CustomEvent('linkflare-reminder', { 
        detail: { 
            title: `Reminder: ${link.title || 'Link'}`,
            body: `It's time for: ${link.title || link.url}`,
            url: link.url,
            id: link.id
        } 
    }))

    if (Notification.permission === 'granted') {
      try {
        // Try Service Worker first (more reliable on some platforms)
        if (navigator.serviceWorker && navigator.serviceWorker.ready) {
             navigator.serviceWorker.ready.then(registration => {
                 registration.showNotification('Linkflare Reminder', {
                    body: `It's time for: ${link.title || link.url}`,
                    icon: '/pwa-192x192.png',
                    tag: `reminder-${link.id}`,
                    data: { url: link.url }
                 }).catch(err => {
                     console.error('[ReminderService] SW Notification failed, falling back to new Notification()', err)
                     this.fallbackNotification(link)
                 })
             })
             // Assume handled via promise
             return true
        } else {
             return this.fallbackNotification(link)
        }
      } catch (e) {
        console.error('Notification error:', e)
        return true // Mark as handled to avoid loops if error is permanent
      }
    } else if (Notification.permission === 'denied') {
        // Permission denied, nothing we can do. Consider it handled so we don't spam checks.
        return true
    } else {
        // Permission is 'default' (not yet asked/answered).
        // Return false so we don't mark as processed, and retry next check.
        // Also log a warning so developer/user knows why it's silent.
        console.warn('Reminder pending - Notification permission not granted yet.')
        return false
    }
  }

  fallbackNotification(link) {
      try {
        const n = new Notification('Linkflare Reminder', {
            body: `It's time for: ${link.title || link.url}`,
            icon: '/pwa-192x192.png',
            tag: `reminder-${link.id}`,
            data: { url: link.url }
        })
        
        n.onclick = function(e) {
            e.preventDefault()
            window.focus()
            if (link.url) window.open(link.url, '_blank')
            n.close()
        }
        return true
      } catch (e) {
          console.error('[ReminderService] Fallback Notification failed', e)
          return true
      }
  }
  
  saveProcessedState() {
      // Keep only last 200 entries
      if (this.processedReminders.size > 200) {
          const arr = Array.from(this.processedReminders).slice(-200)
          this.processedReminders = new Set(arr)
      }
      localStorage.setItem('notifiedReminders', JSON.stringify(Array.from(this.processedReminders)))
  }
}

export default new ReminderService()
